import { useEffect, useState } from 'react';
import {
  isReportRunKind,
  type DecisionSummary,
  type OrgResearchDepth,
  type RunDetail,
} from '@ni/shared';
import { useResuming } from '../../api/app-context';
import { useCancelRun, useResumeRun, useResumeShortfall, useRunCreditPrice } from '../../api/hooks';
import { hasCode, resumeErrorLine } from '../../api/errors';
import {
  isCancelledRun,
  isResumingWindow,
  isTerminal,
  isWithdrawnRun,
  isWorkingState,
  offersResume,
} from '../../lib/run-state';
import { assertNever } from '../../lib/assert-never';
import {
  FACET_PHASE_LABELS,
  facetLabel,
  ORG_LOOKUP_DONE,
  RUN_UNDELIVERED_HEAD,
  undeliveredLine,
  ORG_LOOKUP_FAILED,
  orgLookupRunningLead,
  reviewBeforeBriefLine,
  RUN_CANCEL,
  RUN_CONFIRM_IDENTITY,
  RUN_MORE_DETAIL,
  RESUME_STALLED_LINE,
  RUN_STOPPED,
  timelineLabel,
} from '../../lib/copy';
import { plural } from '../../lib/format';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';
import { ConfirmModal } from '../ui/confirm-modal';
import './narration.css';

/**
 * **The run controls and the vocabulary behind the run block** (`run-block.tsx`):
 * the status line (`stripLine`, also the report screen's run line), the Stop
 * rule, the facet ledger, the dedup line, the timeline rows, and
 * `useRunControls` with the Resume and Stop pieces built on it. The tinted strip
 * that used to live here is gone; every run surface is the run block now.
 *
 * **Staleness.** Silence in `running` gets a quiet marker that says only what is
 * true — how long nothing has reported in. It is narration, never a verdict: the
 * reaper remains the sole liveness authority, and the client never flips a
 * state, offers a recovery it cannot perform, or nags.
 */

/**
 * How long `running` may go without a heartbeat before the strip says so. The
 * pipeline beats every 60s from a background task, so three missed beats is the
 * threshold — comfortably past a slow beat, well short of the reaper's window,
 * and coarse enough that clock skew between the browser and the server cannot
 * manufacture a silence on its own.
 */
const SILENT_AFTER_MS = 3 * 60_000;

/** The re-render that keeps the minute count moving when the poll returns no news. */
const SILENCE_TICK_MS = 30_000;

/**
 * Whole minutes since the run last reported in, or null while that is inside the
 * threshold. Coarse on purpose: the office is being told the shape of a silence,
 * not timing it.
 */
function silentMinutes(heartbeatAt: string): number | null {
  const at = new Date(heartbeatAt).getTime();
  if (Number.isNaN(at)) return null;
  const elapsed = Date.now() - at;
  if (elapsed < SILENT_AFTER_MS) return null;
  return Math.floor(elapsed / 60_000);
}

function facetProgress(run: RunDetail): string | null {
  const planned = run.facet_map.planned.length;
  if (planned === 0) return null;
  // Completions accumulate across rounds; unfiltered, round 2 would read "8/6".
  const done = run.facet_map.completions.filter(
    (completion) => completion.round === run.current_round,
  ).length;
  return `${done}/${planned} facets`;
}

/**
 * **How deep an organisation lookup is going**, read off the plan it was seeded
 * with (organisations-as-subjects note §1 R3a).
 *
 * The depth rides `config.depth`, and **run config never reaches the client**:
 * `run-read.service.ts` says so outright — the dashboard asks what a run is
 * doing, never how it was configured. What does reach it is `facet_map.planned`,
 * seeded at creation from the same depth plan (`plannedFacetsOf`), and the two
 * plans have different lengths by ruling rather than by accident: a lookup is
 * the one site, an investigation is that plus filings, finances and people. So
 * the count is the depth, exactly, and reading it here is cheaper and less
 * fragile than widening the polled read to carry a word the strip is the only
 * reader of.
 *
 * An older row with an empty plan reads as `lookup`, the cheaper of the two —
 * the same direction {@link DEFAULT_ORG_RESEARCH_DEPTH} defaults in.
 */
export function orgLookupDepth(run: RunDetail): OrgResearchDepth {
  return run.facet_map.planned.length > 1 ? 'investigation' : 'lookup';
}

/**
 * The line itself, exported so it can be read against `isResumingWindow` without
 * standing the whole strip up: the interesting claim is not that `resuming`
 * prints "Resuming…" — it is *which runs are resuming*, and that composition is
 * what the spec beside this file asserts.
 *
 * `subjectName` is the organisation's, and only an organisation's: a lookup's
 * line names the place it is about ("Looking up Medici"), where a person's run
 * sits on that person's own page under their name in the header. The record page
 * hands it down; every other caller passes nothing and the line says "the
 * organisation".
 */
export function stripLine(
  run: RunDetail,
  resuming: boolean,
  openQuestions: number,
  subjectName: string | null = null,
): string {
  /* Ahead of the state switch on purpose: the window is precisely the case
     where the state has **not** moved yet. Every gating decision is answered and
     the continuation is enqueued, so the run reads `awaiting_*` for the length
     of a dispatch plus a cold start — and since `run.gated` carries that off the
     polled read, the line survives a reload rather than reverting to the
     question the office already answered. */
  if (resuming) return 'Resuming…';

  switch (run.state) {
    case 'created':
      return 'Starting…';
    case 'running': {
      const note = run.progress?.note ?? run.progress?.stage ?? null;
      if (run.kind === 'quick_search') {
        return note ? `Searching · ${note}` : 'Searching…';
      }
      /*
        A prose rerun runs one phase and plans no facets, so neither half of the
        research composition below applies to it: the fraction renders nothing
        (`planned` is empty by design) and the rounds counter would show
        "Researching · … · round 0" for a run that researches nothing and has no
        rounds. Its own line is the note and nothing else.
      */
      if (run.kind === 'prose_refresh') {
        return note ? `Rewriting the brief · ${note}` : 'Rewriting the brief…';
      }
      /* A report run has no facets and no rounds either (reports design §4.6):
         its line is what it is doing, in the note the workflow posts. */
      if (isReportRunKind(run.kind)) {
        return note ? `Preparing the report · ${note}` : 'Preparing the report…';
      }
      /*
        **An organisation lookup, and never "round N"** (note §1 R3a). Both
        depths are `max_rounds: 0` by ruling, so a rounds counter here would be
        a number that is always 0 on a run that has no follow-up pass to
        describe — the same falsehood the prose rerun's own arm above exists to
        avoid. What it says instead is the depth, the place, and how far through
        the fan-out it is: one leg at a lookup, four at an investigation, and the
        fraction is the same `facet_map` composition a dossier's is, because the
        server seeds the plan from `config.facets` for exactly that reason.
      */
      if (run.kind === 'event_scan') {
        return note ? `Scanning attendees · ${note}` : 'Scanning attendees…';
      }
      if (run.kind === 'event_research') {
        const parts = ['Researching the event'];
        const facets = facetProgress(run);
        if (facets) parts.push(facets);
        if (note) parts.push(note);
        return parts.join(' · ');
      }
      if (run.kind === 'org_lookup') {
        const parts = [orgLookupRunningLead(orgLookupDepth(run), subjectName)];
        const facets = facetProgress(run);
        if (facets) parts.push(facets);
        if (note) parts.push(note);
        return parts.join(' · ');
      }
      /*
        The counter is round 0 only, on purpose. `planned` holds the run's
        INITIAL plan, and a targeted round re-runs a subset of it — so "N/M"
        against the full plan would read as though the later round owed all six
        facets when it owes two. (`current_round` only advances when a
        facet_complete lands, so it lags the pipeline by a facet; the gate keys
        off the projected value, which is the same value the numerator filters
        on, so the pair is always self-consistent.)

        Older rows, created before `facet_map.planned` was seeded, have an empty
        plan and fall through to the note-only composition below.
      */
      const facets = run.current_round === 0 ? facetProgress(run) : null;
      /* A Refresh is one pass, so it has no rounds to count, and it says what
         it is doing rather than "Researching". */
      if (run.research_pass === 'refresh') {
        const parts = ['Refreshing'];
        if (facets) parts.push(facets);
        if (note) parts.push(note);
        return parts.join(' · ');
      }
      const parts = facets ? [facets] : ['Researching'];
      if (note) parts.push(note);
      parts.push(`round ${run.current_round}`);
      return parts.join(' · ');
    }
    case 'checkpointed':
      /*
        A deliberate stop at a phase boundary: the leg banked its work and handed
        off, and the successor is already enqueued. Nothing is asked of the
        office and nothing is wrong, so the line says the run is continuing —
        which is also what keeps the silence marker below honest, since this is
        precisely the case that used to look like a run gone quiet.
      */
      return 'Continuing…';
    case 'awaiting_identity_pick':
      return RUN_CONFIRM_IDENTITY;
    case 'awaiting_prose':
      return RUN_MORE_DETAIL;
    case 'awaiting_adjudication':
      /*
        The run is stopped, not working, and since 2026-08-31 (§3f) it is
        stopped on **every** open question — queued keep/leave-out calls as well
        as blocking ones. "Paused" is the word for that: the office reads it as
        a state the run is in and they are the ones holding it, which is the
        truth. The count comes off the widened `adjudications` counter, so it
        says how many answers the continuation is actually waiting on.
      */
      return `Paused · ${reviewBeforeBriefLine(openQuestions)}`;
    case 'failed':
      // A withdrawn run is not a failure to report on: the office ended it, and
      // the reason is the answer it just gave. No message tail, no Resume.
      if (isWithdrawnRun(run)) return 'Stopped · wrong person';
      /* And neither is a run somebody pressed Cancel on (owner ruling,
         2026-09-10). "Research failed" over a run the office stopped on purpose
         would be the app reporting their own decision back to them as bad news.
         Same sentence the server wrote onto the run and onto its timeline, so
         the line, the error and the history all say one thing. */
      if (isCancelledRun(run)) return RUN_STOPPED;
      {
        const failed =
          run.kind === 'prose_refresh'
            ? 'Rewrite failed'
            : isReportRunKind(run.kind)
              ? 'Report not prepared'
              : run.kind === 'org_lookup'
                ? ORG_LOOKUP_FAILED[orgLookupDepth(run)]
                : run.kind === 'event_research'
                  ? 'Event research failed'
                  : run.kind === 'event_scan'
                    ? 'Scan failed'
                    : run.research_pass === 'refresh'
                      ? 'Refresh failed'
                      : 'Research failed';
        return run.error ? `${failed} · ${run.error.message}` : failed;
      }
    case 'complete':
      /* **No surface mounts the strip on a completed run any more.** The
         person's tab never did, and the organisation's record stopped on the
         owner's ruling of 2026-09-09 ("The status bar once a lookup is complete
         stays put at 'Lookup done' — it should just go away"). This arm is
         therefore vocabulary completeness: it is what the switch has to say to
         stay exhaustive, and what a strip would say if one were ever held open
         over a finished run.

         "Lookup done" and never "Dossier ready": a dossier is a person's, and
         what a lookup finished with is two to four sentences and a leadership
         list. The switch is exhaustive rather than defaulting for that reason —
         the old `default` filed every new kind under the dossier's word. */
      if (run.delivered === false) return `${RUN_UNDELIVERED_HEAD} · ${undeliveredLine(run.kind)}`;
      switch (run.kind) {
        case 'quick_search':
          return 'Quick search done';
        case 'prose_refresh':
          return 'Brief rewritten';
        case 'org_lookup':
          return ORG_LOOKUP_DONE[orgLookupDepth(run)];
        case 'research':
          return run.research_pass === 'refresh' ? 'Refresh done' : 'Research done';
        case 'event_research':
          return 'Event research done';
        case 'event_scan':
          return 'Scan done';
        case 'report_generate':
        case 'report_update':
        case 'report_refresh':
        case 'report_revise':
          return 'Report done';
        default:
          return assertNever(run.kind, 'run strip complete line');
      }
    default:
      return assertNever(run.state, 'run strip line');
  }
}

/**
 * **Whether the strip offers a way to stop this run** (owner ruling, 2026-09-10:
 * "We should have a cancel button on runs. Should be next to the status
 * indicator").
 *
 * Non-terminal, and nothing else — the server's own `isLiveRunState`, so the
 * control and the door behind it answer the same question from the same rule. It
 * covers more than the working states on purpose: a run parked at
 * `awaiting_adjudication` has an invocation waiting to be dispatched the moment
 * the last question is answered, and "I do not want to pay for the rest of this"
 * is a decision the office is *more* likely to reach while looking at a pause
 * than while watching work go by. `created` is in for the plainest reason of
 * all — that is the state the owner's own press sat in for the seconds he had no
 * button.
 *
 * Exported so the spec can assert the rule rather than hunt for a button in
 * markup: which runs offer the control is the claim, and where the control is
 * rendered is a detail of this file.
 */
export function showsCancel(run: RunDetail): boolean {
  return !isTerminal(run.state);
}

/**
 * One row per **planned** facet, so the ledger has the shape of the plan from
 * the first moment rather than growing as the pipeline reports in. A facet the
 * pipeline has said nothing about is `pending`. The round tag appears only past
 * round 0, where a later round re-runs a subset. A person's run only: a lookup's
 * legs are not steps the office watches (owner ruling, 2026-09-10).
 */
export function FacetLedger({ run }: { run: RunDetail }) {
  const planned = run.facet_map.planned;
  if (planned.length === 0) return null;
  const progress = run.facet_progress;

  return (
    <div className="facet-ledger">
      {planned.map((facet) => {
        const entry = progress?.[facet] ?? null;
        const phase = entry?.phase ?? 'pending';
        const docs = entry?.docs ?? 0;
        const round = entry?.round ?? 0;

        return (
          <div className="act-row" key={facet}>
            <div className="act-name">{facetLabel(facet)}</div>
            <div className={phase === 'complete' ? 'act-state done' : 'act-state'}>
              {FACET_PHASE_LABELS[phase]}
            </div>
            <div className="act-value fill">
              {docs > 0 ? plural(docs, 'document', 'documents') : null}
              {round > 0 ? <span className="tag-faint">round {round}</span> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * What the run's deduplication did, when it did anything. `resolutions` counts
 * every duplicate the run settled; the subset an adjudicator model decided reads
 * as "needed a closer look", because what the office is being told is that those
 * pairs were not obvious — not which machinery read them.
 */
export function DedupLine({ dedup }: { dedup: NonNullable<RunDetail['dedup']> }) {
  if (dedup.resolutions === 0) return null;
  const resolved = plural(
    dedup.resolutions,
    'repeated finding combined',
    'repeated findings combined',
  );
  return (
    <div className="tag-faint block">
      {dedup.llm_adjudicated > 0
        ? `${resolved} · ${dedup.llm_adjudicated} checked closely`
        : resolved}
    </div>
  );
}

/**
 * The run's own timeline, collapsed. `created_at` is the first row — the run
 * exists before anything reports on it — and `stage_history` supplies the rest
 * in the order the server appended them. Labels are plain words from one lookup;
 * an id this build has never met is humanized rather than dropped, because a
 * timeline with a hole in it is worse than one with an ugly row.
 */
export function timelineRows(run: RunDetail) {
  return [
    { key: 'created', label: timelineLabel('created'), at: run.created_at },
    ...run.stage_history.map((entry, index) => ({
      key: `${index}-${entry.t}-${entry.v}`,
      label: timelineLabel(entry.v),
      at: entry.at,
    })),
  ];
}

/**
 * **Everything a run surface does, without how it looks:** the Resume window and
 * its stall, the Stop confirmation, the questions a parked run is waiting on and
 * the silence marker. The run block lays them out; keeping them here keeps the
 * report line and the block from holding two opinions about the same run.
 */
export function useRunControls(
  subjectId: string,
  run: RunDetail,
  pendingDecisions: DecisionSummary[],
) {
  const { isMarked, clearResuming } = useResuming();
  const resume = useResumeRun(subjectId);
  const cancel = useCancelRun(subjectId);
  /* A failed run resumed on an enrolled account is held again at its kind's price,
     which the press sends; the failure returned the first hold, so the button never
     shows a price (ruled 2026-09-30, credits launch spec, steps 1–2). */
  const price = useRunCreditPrice(run.id, run.resume !== null);
  const resumePrice = price.units;
  const short = useResumeShortfall(resumePrice);
  /* Stopping a run costs the office everything the rest of it would have found,
     so it carries a confirmation that says what actually happens — the same
     grammar the wrong-person confirm uses one page over. */
  const [stopping, setStopping] = useState(false);

  const marked = isMarked(run.id, run.state);
  const resuming = isResumingWindow(run, marked);
  /* A pressed Resume the agent never claimed. The server says so once the claim
     is overdue (`run.resume`); the run is still failed and Resume is offered
     again. */
  const stalled = run.resume?.status === 'not_picked_up';

  // The mark is only good for the state it was made in, and only the pick and
  // prose answers make one: a run that moved on, or failed instead of resuming,
  // ends it.
  useEffect(() => {
    if (run.state === 'running' || run.state === 'complete' || run.state === 'failed') {
      clearResuming(run.id);
    }
  }, [run.id, run.state, clearResuming]);

  const forThisRun = pendingDecisions.filter((decision) => decision.run_id === run.id);
  /*
    Every open question the run is parked on, blocking and queued alike (§3f).
    The blocking filter that used to live here dated from the world where queued
    rows never held a run: they do now, the continuation waits on all of them,
    and a panel that listed only the blockers would leave the office staring at
    a paused run with nothing left to answer on screen.

    Scoping is by `run_id`, unchanged and load-bearing: queued rows from prior
    runs, and hand-written rows carrying no run at all, are questions about the
    dossier as it stands and keep their inline home at the fact they gate.
  */
  const openAdjudications = forThisRun.filter((decision) => decision.kind === 'claim_adjudication');
  const pickId = forThisRun.find((decision) => decision.kind === 'identity_pick')?.id ?? null;
  const proseId =
    forThisRun.find((decision) => decision.kind === 'identity_more_prose')?.id ?? null;
  const onchainPickId =
    forThisRun.find((decision) => decision.kind === 'onchain_entity_pick')?.id ?? null;

  const counter = run.adjudications;
  // The counter is the authority — it is server-computed on the polled read and
  // covers rows the 60s record poll has not caught up with yet. The list length
  // stands in only for a run old enough to predate the counter.
  const openQuestions = counter
    ? Math.max(counter.total - counter.answered, 0)
    : openAdjudications.length;

  /*
    The silence marker has to keep counting when the poll brings back nothing
    new: a genuinely stalled run answers every 3s with a byte-identical row, and
    react-query's structural sharing means that is not a re-render. This tick is
    what moves the minutes on a run that has stopped saying anything — the one
    case the marker exists for. It runs only while `running`, which is the only
    state the marker appears in and a polled one.
  */
  const [, setTick] = useState(0);
  useEffect(() => {
    if (run.state !== 'running') return;
    const timer = setInterval(() => setTick((value) => value + 1), SILENCE_TICK_MS);
    return () => clearInterval(timer);
  }, [run.state]);

  // Narration is the pipeline's, so a resuming window (which is the client's own
  // optimism about a state that has not flipped yet) shows none of it.
  const silentFor = run.state === 'running' && !resuming ? silentMinutes(run.heartbeat_at) : null;

  return {
    resume,
    cancel,
    price,
    resumePrice,
    short,
    stopping,
    setStopping,
    resuming,
    stalled,
    openAdjudications,
    pickId,
    proseId,
    onchainPickId,
    openQuestions,
    silentFor,
    working: isWorkingState(run.state),
    cancellable: showsCancel(run),
    /* Resume is offered on a run the server would resume (`run.resume`), and
       not while one it accepted is still waiting for the agent. A withdrawal, a
       cancel and an unreadable identity match read `resume: null`: the server
       refuses a Resume on all three. */
    resumable: offersResume(run),
  };
}

export type RunControls = ReturnType<typeof useRunControls>;

/**
 * The Stop confirmation and the refusal line behind it. The gate covers a
 * confirm already open when the run ends by itself.
 */
export function StopRun({ run, controls }: { run: RunDetail; controls: RunControls }) {
  const { cancel, stopping, setStopping, cancellable } = controls;
  return (
    <>
      {/* The refusal, under the line that earned it. `RUN_NOT_CANCELLABLE` is
          not shown: it means the run stopped a moment before the press, the
          strip is about to say so itself, and an error line about a race the
          office cannot see is noise where a plain outcome is arriving. */}
      {cancel.isError && !hasCode(cancel.error, 'RUN_NOT_CANCELLABLE') ? (
        <ErrorLine error={cancel.error} />
      ) : null}
      {stopping && cancellable ? (
        <ConfirmModal
          title={RUN_CANCEL.confirmHead}
          line={RUN_CANCEL.confirmLine}
          confirmLabel={RUN_CANCEL.confirm}
          workingLabel={RUN_CANCEL.working}
          working={cancel.isPending}
          onConfirm={() => cancel.mutate(run.id, { onSuccess: () => setStopping(false) })}
          dismissLabel={RUN_CANCEL.dismiss}
          onClose={() => setStopping(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Resume, unpriced: a failure never reads as a charge. The press still sends the
 * held price, so the button and the hold agree without the button saying it.
 */
export function ResumeAction({
  run,
  controls,
  variant = 'go',
}: {
  run: RunDetail;
  controls: RunControls;
  variant?: 'go' | 'act';
}) {
  const { resume, price, resumePrice, short, stalled } = controls;
  return (
    <>
      {/*
        Resume is enqueue-and-return: the POST 200s with the run still
        `failed`, and the state only flips when the dispatched leg's invocation
        claims it, minutes later. The response already reads `resume: queued`,
        and `useResumeRun` writes it into the polled run, so the block says
        "Resuming…" from the server's own answer and this button goes away until
        the server says the resume started or was never picked up.
      */}
      <WorkingButton
        label="Resume"
        variant={variant}
        disabled={price.pending || short !== null}
        workingLabel="Resuming…"
        working={resume.isPending}
        onClick={() =>
          resume.mutate(
            resumePrice === undefined
              ? run.id
              : { runId: run.id, expectedCreditUnits: resumePrice },
          )
        }
      />
      {short ? <span className="credit-short">{short}</span> : null}
      {resume.isError && !hasCode(resume.error, 'RUN_NOT_RESUMABLE') ? (
        <div className="src">{resumeErrorLine(resume.error)}</div>
      ) : null}
      {stalled ? <div className="src">{RESUME_STALLED_LINE}</div> : null}
    </>
  );
}
